import classNames from 'classnames';
import { forwardRef, type PropsWithChildren, useId } from 'react';

import styles from './ProductListing.module.scss';

export const ProductListingGroup = forwardRef<
    HTMLUListElement,
    PropsWithChildren<{
        className?: string;
        groupName: string | undefined;
        headingAs?: 'p' | 'h2';
        bordersAt?: 'top' | 'bottom' | 'both';
        allowImperativeListFocus?: boolean;
    }>
>((props, ref) => {
    const id = useId();

    const HeadingComponent = props.headingAs || 'p';

    return (
        <div
            className={classNames(
                styles.Group,
                !!props.bordersAt && styles.GroupBorders,
                props.bordersAt === 'top' && styles.GroupBordersTop,
                props.bordersAt === 'bottom' && styles.GroupBordersBottom,
                props.bordersAt === 'both' &&
                    classNames(styles.GroupBordersTop, styles.GroupBordersBottom),
                props.className,
            )}
        >
            <HeadingComponent id={id} className={styles.GroupHeading}>
                {props.groupName}
            </HeadingComponent>
            <ProductListing
                ref={ref}
                aria-labelledby={id}
                ignoreBottomBorder
                allowImperativeFocus={props.allowImperativeListFocus}
            >
                {props.children}
            </ProductListing>
        </div>
    );
});

interface ProductListingProps extends React.HTMLAttributes<HTMLUListElement> {
    allowImperativeFocus?: boolean;
    ignoreBottomBorder?: boolean;
}

const ProductListing = forwardRef<HTMLUListElement, ProductListingProps>(
    ({ ignoreBottomBorder, allowImperativeFocus, ...props }, ref) => {
        return (
            <ul
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                ref={ref}
                className={classNames(
                    styles.List,
                    ignoreBottomBorder && styles.IgnoreBottomBorder,
                    props.className,
                )}
                tabIndex={allowImperativeFocus ? -1 : undefined}
            >
                {props.children}
            </ul>
        );
    },
);

export default ProductListing;
